/**
 * Identité de l'entreprise : nom du logiciel, nom de l'entreprise et logo.
 * Réglée dans Administration > Identité de l'entreprise. Les valeurs sont injectées par
 * resources/views/app.blade.php (window.__BRANDING__) : elles sont disponibles dès le premier rendu.
 *
 * - `branding.appName` : nom du logiciel (navigation, titres d'onglet, métadonnées des fichiers) ;
 * - `branding.companyName` : nom de l'entreprise (en-têtes des exports, signatures) ;
 * - `branding.logoUrl` : adresse du logo, ou null.
 */
import { reactive } from 'vue'

const initial = (typeof window !== 'undefined' && window.__BRANDING__) || {}

export const branding = reactive({
  appName: initial.app_name || 'ERP BTP',
  companyName: initial.company_name || initial.app_name || 'ERP BTP',
  logoUrl: initial.logo_url || null,
  hasCustomLogo: Boolean(initial.has_custom_logo)
})

let logoPromise = null

/** Applique la réponse de /api/branding (après enregistrement dans l'administration). */
export function applyBranding(data = {}) {
  branding.appName = data.app_name || branding.appName
  branding.companyName = data.company_name || branding.appName
  branding.logoUrl = data.logo_url || null
  branding.hasCustomLogo = Boolean(data.has_custom_logo)
  logoPromise = null
}

/** Nom de l'entreprise en capitales, pour les bandeaux de titre des exports. */
export const companyNameUpper = () => branding.companyName.toLocaleUpperCase('fr-FR')

/**
 * Logo prêt pour ExcelJS / jsPDF : { buffer, extension ('png' | 'jpeg'), mime, dataUrl, width, height }.
 * Null sans logo. Le résultat est mis en cache jusqu'au prochain changement d'identité.
 */
export function loadBrandLogo() {
  if (!branding.logoUrl) return Promise.resolve(null)
  if (!logoPromise) {
    logoPromise = fetchLogo(branding.logoUrl).catch(() => null)
  }
  return logoPromise
}

async function fetchLogo(url) {
  const response = await fetch(url, { credentials: 'same-origin' })
  if (!response.ok) return null
  const buffer = await response.arrayBuffer()
  // Type lu dans les premiers octets : une page d'erreur HTML ne doit jamais être prise pour une image
  const head = new Uint8Array(buffer.slice(0, 4))
  const isPng = head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47
  const isJpeg = head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff
  if (!isPng && !isJpeg) return null
  const mime = isPng ? 'image/png' : 'image/jpeg'
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(new Blob([buffer], { type: mime }))
  })
  const { width, height } = await new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => resolve({ width: 0, height: 0 })
    img.src = dataUrl
  })
  return { buffer, extension: isPng ? 'png' : 'jpeg', mime, dataUrl, width, height }
}

/** Dimensions du logo dans un cadre maxWidth × maxHeight, proportions conservées. */
export function fitLogo(logo, maxWidth, maxHeight) {
  if (!logo?.width || !logo?.height) return { width: maxWidth, height: maxHeight }
  const ratio = Math.min(maxWidth / logo.width, maxHeight / logo.height)
  return { width: Math.round(logo.width * ratio), height: Math.round(logo.height * ratio) }
}
